import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Printer, Send } from "lucide-react";

const BROKER_NAME = "Equitas Residential";
const BROKER_DRE = "01151598"; // Hardcoded default for Equitas Residential (also set BROKER_DRE_LICENSE env on backend if overriding)
const LOGO_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/m8laadke_equitasfit%20%28Logo%29%20%285%29.png";

function fmtMoney(n) {
  if (isNaN(n) || n === null || n === undefined) return "$0.00";
  return "$" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseNum(v) {
  const n = parseFloat(String(v || "").replace(/[$,\s]/g, ""));
  return isNaN(n) ? 0 : n;
}
function formatWithCommas(v) {
  const raw = String(v || "").replace(/[^\d.]/g, "");
  if (!raw) return "";
  const [int, dec] = raw.split(".");
  const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dec !== undefined ? `${withCommas}.${dec}` : withCommas;
}
function fmtDateISO(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
}

export default function CommissionDemandDialog({ open, onOpenChange, listing, onListingUpdate }) {
  const [salesPriceStr, setSalesPriceStr] = useState("");
  const [commissionRate, setCommissionRate] = useState("2.5");
  const [creditStr, setCreditStr] = useState("");
  const [escrowEmail, setEscrowEmail] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("wire");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !listing) return;
    setEscrowEmail(listing.escrow_officer_email || "");
    setSubject(
      `Commission Demand · ${listing.property_address || "listing"} · Escrow #${listing.escrow_number || "TBD"}`
    );
    setBody(
      `Please find the commission demand for ${listing.property_address || "the above property"}. Kindly acknowledge receipt and confirm inclusion in the closing disclosure.`
    );
  }, [open, listing]);

  const salesPrice = useMemo(() => parseNum(salesPriceStr), [salesPriceStr]);
  const rate = useMemo(() => parseNum(commissionRate), [commissionRate]);
  const credit = useMemo(() => parseNum(creditStr), [creditStr]);
  const gross = useMemo(() => Math.round(salesPrice * rate) / 100, [salesPrice, rate]);
  const net = useMemo(() => Math.max(0, Math.round((gross - credit) * 100) / 100), [gross, credit]);

  if (!listing) return null;

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) {
      toast.error("Popup blocked — allow popups to print/preview.");
      return;
    }
    w.document.write(renderPrintableHtml({
      listing, salesPrice, rate, credit, gross, net, paymentMethod,
    }));
    w.document.close();
    setTimeout(() => { try { w.print(); } catch {} }, 350);
  };

  const handleEmail = async () => {
    if (!escrowEmail.trim()) return toast.error("Enter the escrow officer's email.");
    if (salesPrice <= 0) return toast.error("Enter the final sales price.");
    if (rate <= 0) return toast.error("Enter the commission rate.");
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/commission-demand`, {
        sales_price: salesPrice,
        commission_rate: rate,
        authorized_credit: credit,
        escrow_email: escrowEmail.trim(),
        subject: subject.trim(),
        body: body.trim(),
        payment_method: paymentMethod,
        mark_done: true,
      });
      toast.success(`Sent · Net ${fmtMoney(data.net_commission)}`);
      // Refresh listing so the task shows as done with new notes
      const { data: fresh } = await api.get(`/listings/${listing.id}`);
      onListingUpdate?.(fresh);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="commission-demand-dialog"
        className="sm:max-w-3xl bg-[#faf7f2] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <p className="overline text-stone-500 mb-1">Phase 07 · Escrow</p>
          <DialogTitle className="font-heading font-semibold tracking-tight text-2xl text-stone-900">
            Commission Demand
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Standard Equitas Residential disbursement request — auto-populated from this home. Edit, preview, then email to escrow.
          </DialogDescription>
        </DialogHeader>

        {/* Section 1 — Transaction & Escrow Info (auto) */}
        <section className="rounded-md border border-stone-200 bg-white p-5">
          <p className="overline text-stone-500 mb-3">1. Transaction & Escrow</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <FieldRO label="Property Address" value={listing.property_address || "—"} />
            <FieldRO label="Seller(s)" value={listing.seller_names || "—"} />
            <FieldRO label="Target Closing" value={fmtDateISO(listing.closing_date)} />
            <FieldRO label="Escrow Company" value={listing.escrow_company || "—"} />
            <FieldRO label="Escrow Officer" value={listing.escrow_officer_name || "—"} />
            <FieldRO label="Escrow / File No." value={listing.escrow_number || "—"} mono />
            <FieldRO label="Broker Payee" value={BROKER_NAME} />
          </dl>
        </section>

        {/* Section 2 — Commission math */}
        <section className="rounded-md border border-stone-200 bg-white p-5">
          <p className="overline text-stone-500 mb-3">2. Commission Due & Disbursement</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-stone-700 text-xs">Final Sales Price ($)</Label>
              <Input
                data-testid="cd-sales-price"
                value={salesPriceStr}
                onChange={(e) => setSalesPriceStr(formatWithCommas(e.target.value))}
                placeholder="e.g., 1,250,000"
                className="h-10 bg-white border-stone-300"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-stone-700 text-xs">Commission Rate (%)</Label>
              <Input
                data-testid="cd-rate"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                placeholder="2.5"
                className="h-10 bg-white border-stone-300"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-stone-700 text-xs">Authorized Credit ($)</Label>
              <Input
                data-testid="cd-credit"
                value={creditStr}
                onChange={(e) => setCreditStr(formatWithCommas(e.target.value))}
                placeholder="Optional"
                className="h-10 bg-white border-stone-300"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-stone-100 space-y-2 text-sm">
            <SumRow label="Commission Earned" value={fmtMoney(gross)} />
            <SumRow label="Authorized Credits" value={`− ${fmtMoney(credit)}`} muted />
            <div className="flex justify-between items-baseline pt-3 mt-2 border-t-2 border-[#010082]">
              <span className="font-heading font-semibold text-[#010082] uppercase tracking-widest text-xs">Net Commission Payable</span>
              <span data-testid="cd-net-commission" className="font-heading text-2xl font-semibold text-[#010082] tabular-nums">{fmtMoney(net)}</span>
            </div>
          </div>
        </section>

        {/* Section 3 — Payment instructions */}
        <section className="rounded-md border border-stone-200 bg-white p-5">
          <p className="overline text-stone-500 mb-3">3. Payment Instructions</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <FieldRO label="Payee" value={BROKER_NAME} />
            <FieldRO label="DRE License" value={BROKER_DRE} mono />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Preferred Method</div>
              <div className="mt-1.5 inline-flex rounded-full border border-stone-300 bg-white overflow-hidden text-xs">
                {["wire","check"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`cd-method-${m}`}
                    onClick={() => setPaymentMethod(m)}
                    className={`px-4 py-1.5 font-semibold uppercase tracking-widest ${paymentMethod === m ? "bg-[#010082] text-white" : "text-stone-600 hover:bg-stone-50"}`}
                  >
                    {m === "wire" ? "Wire transfer" : "Check"}
                  </button>
                ))}
              </div>
            </div>
          </dl>
          <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
            Wire &amp; deposit instructions on file with escrow. Please confirm before disbursement.
          </p>
        </section>

        {/* Section 4 — Cert & ack */}
        <section className="rounded-md border border-stone-200 bg-white p-5">
          <p className="overline text-stone-500 mb-3">4. Broker Certification &amp; Escrow Acknowledgment</p>
          <p className="text-xs text-stone-700 leading-relaxed">
            The undersigned broker certifies the above commission is due and payable at close of escrow and authorizes escrow to disburse the Net Commission per these instructions.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <SigLine label="Signatory" value={listing.listing_agent || BROKER_NAME} />
            <SigLine label="Date" value={new Date().toLocaleDateString(undefined,{month:'long',day:'numeric',year:'numeric'})} />
            <SigLine label="DRE" value={BROKER_DRE} mono />
          </div>
        </section>

        {/* Email envelope */}
        <section className="rounded-md border border-stone-200 bg-white p-5 space-y-3">
          <p className="overline text-stone-500">Email to Escrow Officer</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-stone-700 text-xs">To</Label>
              <Input
                data-testid="cd-to"
                type="email"
                value={escrowEmail}
                onChange={(e) => setEscrowEmail(e.target.value)}
                className="h-10 bg-white border-stone-300"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-1">
              <Label className="text-stone-700 text-xs">Subject</Label>
              <Input
                data-testid="cd-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="h-10 bg-white border-stone-300"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-stone-700 text-xs">Cover note</Label>
            <Textarea
              data-testid="cd-body"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="bg-white border-stone-300"
            />
          </div>
        </section>

        <div className="flex flex-wrap gap-3 justify-end pt-2">
          <Button
            variant="outline"
            data-testid="cd-print"
            onClick={handlePrint}
            className="border-stone-300"
          >
            <Printer className="h-4 w-4 mr-2" /> Print / PDF preview
          </Button>
          <Button
            data-testid="cd-send"
            onClick={handleEmail}
            disabled={busy}
            style={{ backgroundColor: "#010082" }}
            className="text-white hover:opacity-90"
          >
            <Send className="h-4 w-4 mr-2" /> {busy ? "Sending…" : "Email escrow & mark done"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldRO({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`text-stone-800 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
function FieldRW({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{label}</div>
      <Input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 bg-white border-stone-300"
      />
    </div>
  );
}
function SumRow({ label, value, muted }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className={`text-stone-${muted ? "500" : "700"}`}>{label}</span>
      <span className={`tabular-nums ${muted ? "text-stone-500" : "text-stone-900 font-semibold"}`}>{value}</span>
    </div>
  );
}
function SigLine({ label, value, mono }) {
  return (
    <div className="border-t border-stone-300 pt-1.5">
      <div className={`text-stone-800 text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
    </div>
  );
}

function renderPrintableHtml({ listing, salesPrice, rate, credit, gross, net, paymentMethod }) {
  const mm = fmtMoney;
  const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const closeIso = listing.closing_date || "";
  const closeLabel = closeIso
    ? new Date(`${closeIso}T00:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : "—";
  const today = new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  const methodLabel = paymentMethod === "wire" ? "Wire transfer" : "Check";
  return `<!doctype html><html><head><meta charset="utf-8"><title>Commission Demand · ${esc(listing.property_address || "")}</title>
<style>
  body{font-family:Arial,sans-serif;color:#0a0a0a;padding:32px;max-width:760px;margin:0 auto;background:#faf7f2}
  h1{font-family:Georgia,serif;font-size:24px;margin:16px 0 20px}
  .letterhead{display:flex;justify-content:space-between;align-items:center;gap:24px;margin-bottom:0}
  .letterhead img{width:96px;height:96px;display:block}
  .meta{text-align:right;font-size:11px;line-height:1.5}
  .meta .lbl{font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:#78716c;font-weight:700;margin-top:6px}
  .meta .val{font-weight:700;color:#0a0a0a}
  .rule{height:2px;background:#010082;margin:14px 0 0}
  section{background:#fff;border:1px solid #e7e5e4;border-radius:6px;padding:18px 20px;margin:14px 0}
  .st{font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#010082;font-weight:700;margin-bottom:10px}
  .r{display:flex;justify-content:space-between;gap:16px;padding:6px 0;border-bottom:1px solid #f5f5f4;font-size:13px}
  .r span:first-child{color:#78716c}
  .r span:last-child{font-weight:600;text-align:right}
  .total{display:flex;justify-content:space-between;padding-top:10px;margin-top:8px;border-top:2px solid #010082}
  .total .l{color:#010082;font-weight:700;letter-spacing:.02em}
  .total .v{color:#010082;font-weight:700;font-size:18px}
  @media print{ body{background:#fff} }
</style></head><body>
  <div class="letterhead">
    <img src="${LOGO_URL}" alt="Equitas · 1% List Fee">
    <div class="meta">
      <div class="lbl">Commission Demand</div><div class="val">Equitas Residential</div>
      <div class="lbl">BRE / DRE License</div><div class="val">${BROKER_DRE}</div>
      <div class="lbl">Issued</div><div class="val">${today}</div>
    </div>
  </div>
  <div class="rule"></div>
  <h1>Commission Demand &amp; Disbursement Request</h1>

  <section><div class="st">1. Transaction &amp; Escrow Information</div>
    <div class="r"><span>Property Address</span><span>${esc(listing.property_address || "—")}</span></div>
    <div class="r"><span>Seller(s)</span><span>${esc(listing.seller_names || "—")}</span></div>
    <div class="r"><span>Target Closing Date</span><span>${esc(closeLabel)}</span></div>
    <div class="r"><span>Escrow Company</span><span>${esc(listing.escrow_company || "—")}</span></div>
    <div class="r"><span>Escrow Officer</span><span>${esc(listing.escrow_officer_name || "—")}</span></div>
    <div class="r"><span>Escrow / File No.</span><span>${esc(listing.escrow_number || "—")}</span></div>
  </section>

  <section><div class="st">2. Commission Due &amp; Disbursement Summary</div>
    <div class="r"><span>Final Sales Price</span><span>${mm(salesPrice)}</span></div>
    <div class="r"><span>Listing Broker Commission Rate</span><span>${rate}%</span></div>
    <div class="r"><span>Commission Earned</span><span>${mm(gross)}</span></div>
    <div class="r"><span>Authorized Credits</span><span>-${mm(credit)}</span></div>
    <div class="total"><span class="l">Net Commission Payable</span><span class="v">${mm(net)}</span></div>
  </section>

  <section><div class="st">3. Payment Instructions</div>
    <div class="r"><span>Payee</span><span>${BROKER_NAME}</span></div>
    <div class="r"><span>DRE License</span><span>${BROKER_DRE}</span></div>
    <div class="r"><span>Preferred Method</span><span>${methodLabel}</span></div>
  </section>

  <section><div class="st">4. Broker Certification &amp; Escrow Acknowledgment</div>
    <p style="line-height:1.6;font-size:13px">The undersigned broker certifies the above commission is due and payable at close of escrow and authorizes escrow to disburse the Net Commission per these instructions.</p>
    <div class="r"><span>Broker</span><span>${BROKER_NAME} · DRE ${BROKER_DRE}</span></div>
    <div class="r"><span>Signatory</span><span>${esc(listing.listing_agent || BROKER_NAME)}</span></div>
    <div class="r"><span>Date</span><span>${today}</span></div>
  </section>
</body></html>`;
}
